import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Calendar, MapPin, Users, Link, FileText, Sparkles, IndianRupee, UserCircle, Users as GroupIcon, Upload, Image as ImageIcon, Trash2, Plus, Minus, Eye, EyeOff, Check } from 'lucide-react';
import { getData } from '../../../../services/apiService';
import Swal from 'sweetalert2';

const EventFormModal = ({
  isOpen,
  editingEvent,
  formData,
  selectedInstituteCategory,
  setSelectedInstituteCategory,
  onFormDataChange,
  onSubmit,
  onClose,
  eventTypes,
  eventModes,
  participantTypes,
  eventStatuses,
  institutes,
  organizers,

}) => {
  const [isParticipantOpen, setIsParticipantOpen] = useState(false);
  const participantDropdownRef = useRef(null);
  const [eventGroupTypes, setEventGroupTypes] = useState([]);
  const [logoError, setLogoError] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [departmentTypes, setDepartmentTypes] = useState([]);
  // NEW: State for sub-events from API
  const [subEventsList, setSubEventsList] = useState([]);
  const [isLoadingSubEvents, setIsLoadingSubEvents] = useState(false);
  // Sub-events states
  const [showSubEvents, setShowSubEvents] = useState(false);
  const [selectedSubEvents, setSelectedSubEvents] = useState([]);
  const [isSubEventDropdownOpen, setIsSubEventDropdownOpen] = useState(false);
  const subEventDropdownRef = useRef(null);

  // Sub-event participant configuration popup
  const [showSubEventConfigPopup, setShowSubEventConfigPopup] = useState(false);
  const [currentSubEventConfig, setCurrentSubEventConfig] = useState(null);
  const [subEventParticipantsConfig, setSubEventParticipantsConfig] = useState({});
  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  // Filter event types based on group event selection
  const filteredEventTypes = useMemo(() => {
    if (formData.is_group_event === 'yes') {
      return eventTypes.filter(type => type.is_group_event === true);
    } else if (formData.is_group_event === 'no') {
      return eventTypes.filter(type => type.is_group_event === false);
    }
    return eventTypes;
  }, [eventTypes, formData.is_group_event]);

  // MODIFIED: Filter available events to use API data
  const filteredAvailableEvents = useMemo(() => {
    // Use subEventsList from API as the primary source
    const eventsToUse = subEventsList && subEventsList.length > 0
      ? subEventsList.map(item => ({
        id: item.SubEvent_ID,
        Event_Id: item.SubEvent_ID,
        Event_Name: item.name,
        name: item.name,
        Event_Sname: item.short_name,
        short_name: item.short_name
      }))
      : [];

    if (editingEvent && formData.Event_Id) {
      return eventsToUse.filter(event => {
        const eventId = event.id || event.Event_Id;
        return eventId?.toString() !== formData.Event_Id?.toString();
      });
    }
    return eventsToUse;
  }, [subEventsList, editingEvent, formData.Event_Id]);

  // NEW: Fetch sub-events when modal opens
  useEffect(() => {
    const fetchSubEvents = async () => {
      if (!isOpen) return;

      setIsLoadingSubEvents(true);
      try {
        // Use the API endpoint: Event/GetSubEvent
        const response = await getData('/Event/GetSubEvent');

        if (!response?.isError && Array.isArray(response?.data)) {
          // Map the response to a consistent format based on actual API response
          const mappedSubEvents = response.data.map(item => ({
            id: item.SubEvent_ID,                    // Primary ID
            SubEvent_ID: item.SubEvent_ID,            // Keep original
            Event_Id: item.SubEvent_ID,               // For backward compatibility
            Event_Name: item.SubEvent_Name,           // Full name
            name: item.SubEvent_Name,                  // For compatibility
            Event_Sname: item.SubEvent_Name,          // Use full name as short name if no short name provided
            short_name: item.SubEvent_Name             // Use full name as short name
          }));

          setSubEventsList(mappedSubEvents);
        } else {
          console.error('Error fetching sub-events:', response);
          setSubEventsList([]);
        }
      } catch (error) {
        console.error('Error fetching sub-events:', error);
        setSubEventsList([]);
      } finally {
        setIsLoadingSubEvents(false);
      }
    };

    fetchSubEvents();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Reset touched fields
      setTouchedFields({});

      // If editing an event, populate the form with the event data
      if (editingEvent && editingEvent.Event_Id) {
        // Use the editingEvent prop directly instead of formData
        const eventData = editingEvent;

        // Handle sub-events based on HasSubEvents flag and SubEventDetails
        const hasSubEvents = eventData.HasSubEvents === '1';
        setShowSubEvents(hasSubEvents);

        // Process sub-events if they exist
        if (hasSubEvents && eventData.SubEventDetails && eventData.SubEventDetails.length > 0) {
          // Map sub-events to the format expected by the component
          const mappedSubEvents = eventData.SubEventDetails.map((subEvent, index) => {
            // Safely access SubEventId with default value
            const subEventId = subEvent?.SubEventId || subEvent?.SubEvent_Id || 0;

            return {
              id: subEventId.toString(), // Use the actual SubEventId from the API
              SubEvent_ID: subEventId.toString(),
              Event_Id: eventData.Event_Id, // Keep parent event ID
              originalSubEventId: subEventId,
              Event_Name: subEvent?.Name || '',
              name: subEvent?.Name || '',
              Event_Sname: subEvent?.ShortName || subEvent?.Name || '',
              short_name: subEvent?.ShortName || subEvent?.Name || '',
              index: index // Store index for reference
            };
          }).filter(subEvent => subEvent.id !== '0'); // Filter out invalid sub-events

          setSelectedSubEvents(mappedSubEvents);

          // Map participant configurations for each sub-event using actual SubEventId as key
          const subEventConfigs = {};

          eventData.SubEventDetails.forEach((subEvent, index) => {
            // Safely access SubEventId
            const subEventId = subEvent?.SubEventId || subEvent?.SubEvent_Id;
            if (!subEventId) return; // Skip if no valid ID

            const subEventIdStr = subEventId.toString();

            // Safely access Participants array
            const participants = subEvent?.Participants || [];

            // Remove duplicates from Participants array (if any)
            // Using a Map to deduplicate based on Id
            const uniqueParticipantsMap = new Map();
            participants.forEach(item => {
              if (item?.Id) {
                uniqueParticipantsMap.set(item.Id, item);
              }
            });
            const uniqueParticipants = Array.from(uniqueParticipantsMap.values());

            // Map participants for this sub-event
            const selectedParticipants = uniqueParticipants
              .map(p => p?.Id?.toString())
              .filter(id => id); // Filter out undefined/null

            // Map pricing for each participant
            const participantPricing = {};
            uniqueParticipants.forEach(participant => {
              const participantIdStr = participant?.Id?.toString();
              if (participantIdStr) {
                participantPricing[participantIdStr] = {
                  amount: participant?.TotAmt?.toString() || '',
                  max_participants: participant?.MaxParticipants?.toString() || '',
                  unlimited: !participant?.IsLimited // IsLimited false means unlimited
                };
              }
            });

            subEventConfigs[subEventIdStr] = {
              eventId: subEventIdStr,
              originalSubEventId: subEventId,
              eventName: subEvent?.Name || '',
              selectedParticipants: selectedParticipants,
              participantPricing: participantPricing,
              index: index
            };
          });

          setSubEventParticipantsConfig(subEventConfigs);

          // Update form data with sub-events information
          onFormDataChange(prev => ({
            ...prev,
            has_sub_events: 'yes',
            sub_events: mappedSubEvents,
            sub_event_participants: subEventConfigs,
            // Clear participant_type and participant_pricing for sub-event mode
            Participant_type: [],
            participant_pricing: []
          }));
        } else {
          // For events without sub-events
          setShowSubEvents(false);
          setSelectedSubEvents([]);
          setSubEventParticipantsConfig({});

          // Handle regular participant pricing if it exists
          if (eventData.Participants && eventData.Participants.length > 0) {
            // Remove duplicates from Participants array
            const uniqueParticipantsMap = new Map();
            eventData.Participants.forEach(item => {
              if (item?.Participant_TypeId) {
                uniqueParticipantsMap.set(item.Participant_TypeId, item);
              }
            });
            const uniqueParticipants = Array.from(uniqueParticipantsMap.values());

            // Map participants for main event
            const participantIds = uniqueParticipants
              .map(p => p?.Participant_TypeId?.toString())
              .filter(id => id); // Filter out undefined/null

            // Map pricing for main event participants
            const participantPricing = uniqueParticipants
              .map(participant => ({
                participant_id: participant?.Participant_TypeId?.toString(),
                amount: participant?.TotAmt?.toString() || '',
                max_participants: participant?.Max_participant?.toString() || '',
                unlimited: participant?.IsLimited === 0
              }))
              .filter(p => p.participant_id); // Filter out entries without participant_id

            onFormDataChange(prev => ({
              ...prev,
              Participant_type: participantIds,
              participant_pricing: participantPricing
            }));
          }
        }

        // Initialize logo preview if there's a logo URL and it's not "string"
        if (eventData.LogoPath_Url && eventData.LogoPath_Url !== 'string') {
          setLogoPreview(eventData.LogoPath_Url);
          setLogoFile(null);
          setLogoError('');
        } else {
          setLogoPreview(null);
          setLogoFile(null);
        }
      } else {
        // Creating new event - reset all states
        setLogoPreview(null);
        setLogoFile(null);
        setLogoError('');
        setShowSubEvents(false);
        setSelectedSubEvents([]);
        setSubEventParticipantsConfig({});
        setValidationErrors({});
      }
    }
  }, [isOpen, editingEvent]);

  // Mark field as touched
  const markFieldTouched = (field) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
  };

  // Validate single field
  const validateField = (field, value) => {
    const errors = {};

    switch (field) {
      case 'Event_Name':
        if (!value || value.trim().length === 0) {
          errors[field] = 'Event name is required';
        } else if (value.trim().length < 3) {
          errors[field] = 'Event name must be at least 3 characters';
        } else if (value.trim().length > 200) {
          errors[field] = 'Event name must be less than 200 characters';
        }
        break;

      case 'Event_Sname':
        if (!value || value.trim().length === 0) {
          errors[field] = 'Short name is required';
        } else if (value.trim().length < 2) {
          errors[field] = 'Short name must be at least 2 characters';
        } else if (value.trim().length > 50) {
          errors[field] = 'Short name must be less than 50 characters';
        }
        break;

      case 'Inst_Name':
        if (!value || value === '') {
          errors[field] = 'Institute name is required';
        }
        break;

      case 'Dept_id':
        if (!value || value === '') {
          errors[field] = 'Department name is required';
        }
        break;

      case 'Organizer_id':
        if (!value || value === '') {
          errors[field] = 'Event organizer is required';
        }
        break;

      case 'Event_type':
        if (!value || value === '') {
          errors[field] = 'Event type is required';
        }
        break;

      case 'Event_mode':
        if (!value || value === '') {
          errors[field] = 'Event mode is required';
        }
        break;

      case 'Participant_type':
        if (!showSubEvents && (!value || (Array.isArray(value) && value.length === 0))) {
          errors[field] = 'Please select at least one participant type';
        }
        break;

      case 'Start_datetime':
        if (!value) {
          errors[field] = 'Start date is required';
        } else {
          const startDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (startDate < today) {
            errors[field] = 'Start date cannot be in the past';
          }
        }
        break;

      case 'End_datetime':
        if (!value) {
          errors[field] = 'End date is required';
        } else if (formData.Start_datetime) {
          const startDate = new Date(formData.Start_datetime);
          const endDate = new Date(value);
          if (endDate <= startDate) {
            errors[field] = 'End date must be after start date';
          }
        }
        break;

      case 'PaymentStartDate':
        if (!value) {
          errors[field] = 'Payment start date is required';
        } else if (formData.Start_datetime) {
          const paymentStart = new Date(value);
          const eventStart = new Date(formData.Start_datetime);
          if (paymentStart >= eventStart) {
            errors[field] = 'Payment must start before the event';
          }
        }
        break;

      case 'PaymentEndDate':
        if (!value) {
          errors[field] = 'Payment end date is required';
        } else if (formData.PaymentStartDate) {
          const paymentStart = new Date(formData.PaymentStartDate);
          const paymentEnd = new Date(value);
          if (paymentEnd < paymentStart) {
            errors[field] = 'Payment end date must be after start date';
          }
        } else if (formData.Start_datetime) {
          const paymentEnd = new Date(value);
          const eventStart = new Date(formData.Start_datetime);
          if (paymentEnd >= eventStart) {
            errors[field] = 'Payment must end before the event starts';
          }
        }
        break;

      case 'Event_Place':
        if (!value || value.trim().length === 0) {
          errors[field] = 'Event place is required';
        } else if (value.trim().length < 3) {
          errors[field] = 'Event place must be at least 3 characters';
        }
        break;

      case 'Event_Description':
        if (!value || value.trim().length === 0) {
          errors[field] = 'Event description is required';
        } else if (value.trim().length < 10) {
          errors[field] = 'Event description must be at least 10 characters';
        } else if (value.trim().length > 2000) {
          errors[field] = 'Event description must be less than 2000 characters';
        }
        break;

      case 'max_group_size':
        if (formData.is_group_event === 'yes') {
          if (!value || value === '') {
            errors[field] = 'Maximum group size is required for group events';
          } else if (!/^\d+$/.test(value)) {
            errors[field] = 'Group size must be a number';
          } else if (parseInt(value) < 2) {
            errors[field] = 'Group size must be at least 2';
          } else if (parseInt(value) > 100) {
            errors[field] = 'Group size cannot exceed 100';
          }
        }
        break;

      case 'event_group_type':
        if (formData.is_group_event === 'yes' && (!value || value === '')) {
          errors[field] = 'Group event type is required';
        }
        break;

      case 'sub_events':
        if (showSubEvents && (!selectedSubEvents || selectedSubEvents.length === 0)) {
          errors[field] = 'Please select at least one sub-event';
        }
        break;

      default:
        break;
    }

    return errors;
  };

  // Validate entire form
  const validateForm = () => {
    const errors = {};

    // Validate all fields
    const fieldsToValidate = [
      'Event_Name',
      'Event_Sname',
      'Inst_Name',
      'Dept_id',
      'Organizer_id',
      'Event_type',
      'Event_mode',
      'Participant_type',
      'Start_datetime',
      'End_datetime',
      'PaymentStartDate',
      'PaymentEndDate',
      'Event_Place',
      'Event_Description',
      'max_group_size',
      'event_group_type',
      'sub_events'
    ];

    fieldsToValidate.forEach(field => {
      const fieldErrors = validateField(field, formData[field]);
      Object.assign(errors, fieldErrors);
    });

    // Validate based on sub-events selection
    if (showSubEvents) {
      if (!formData.sub_events || formData.sub_events.length === 0) {
        errors.sub_events = 'Please select at least one sub-event';
      } else {
        // Check if all selected sub-events have participant configuration
        const subEventsWithoutConfig = formData.sub_events.filter(subEvent => {
          const config = formData.sub_event_participants?.[subEvent.id];
          return !config || !config.selectedParticipants || config.selectedParticipants.length === 0;
        });

        if (subEventsWithoutConfig.length > 0) {
          errors.sub_event_participants = 'Please configure participants for all selected sub-events';
        }
      }
    } else {
      // Validate participant pricing for non-sub-event mode
      if (formData.Participant_type && formData.Participant_type.length > 0) {
        const participantPricingErrors = [];

        formData.Participant_type.forEach(participantId => {
          const pricing = formData.participant_pricing?.find(p =>
            p.participant_id === participantId.toString() || p.participant_id === participantId
          );

          if (!pricing) {
            participantPricingErrors.push(`Pricing not configured for participant type`);
          } else {
            if (!pricing.amount || pricing.amount.trim() === '') {
              participantPricingErrors.push(`Amount is required for participant type`);
            } else if (!/^\d+(\.\d{1,2})?$/.test(pricing.amount)) {
              participantPricingErrors.push(`Invalid amount format for participant type`);
            } else if (parseFloat(pricing.amount) < 0) {
              participantPricingErrors.push(`Amount cannot be negative`);
            }

            if (!pricing.unlimited && (!pricing.max_participants || pricing.max_participants.trim() === '')) {
              participantPricingErrors.push(`Max participants is required or select unlimited`);
            } else if (!pricing.unlimited && !/^\d+$/.test(pricing.max_participants)) {
              participantPricingErrors.push(`Max participants must be a number`);
            } else if (!pricing.unlimited && parseInt(pricing.max_participants) < 1) {
              participantPricingErrors.push(`Max participants must be at least 1`);
            }
          }
        });

        if (participantPricingErrors.length > 0) {
          errors.participant_pricing = 'Please configure valid pricing and limits for all selected participant types';
        }
      }
    }


    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Helper function to get date from datetime string
  const getDateFromDatetime = (datetimeString) => {
    if (!datetimeString) return '';
    return datetimeString.split('T')[0];
  };

  // Calculate min and max dates for date inputs
  const eventStartDate = useMemo(() => {
    return getDateFromDatetime(formData.Start_datetime);
  }, [formData.Start_datetime]);

  const eventEndDate = useMemo(() => {
    return getDateFromDatetime(formData.End_datetime);
  }, [formData.End_datetime]);

  const paymentStartDate = useMemo(() => {
    return getDateFromDatetime(formData.PaymentStartDate);
  }, [formData.PaymentStartDate]);

  const paymentEndDate = useMemo(() => {
    return getDateFromDatetime(formData.PaymentEndDate);
  }, [formData.PaymentEndDate]);

  // Get today's date in YYYY-MM-DD format for min date
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Handle group event type change
  const handleGroupEventChange = (value) => {
    onFormDataChange(prev => ({
      ...prev,
      is_group_event: value,
      Event_type: '', // Reset event type when group event status changes
      event_group_type: value === 'no' ? '' : prev.event_group_type,
      max_group_size: value === 'no' ? '' : prev.max_group_size
    }));

    // Clear validation errors for group event fields
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.event_group_type;
      delete newErrors.max_group_size;
      return newErrors;
    });
  };

  // Handle max group size change
  const handleMaxGroupSizeChange = (value) => {
    if (!/^\d*$/.test(value)) return;

    if (value.length > 1 && value.startsWith('0')) {
      value = value.replace(/^0+/, '');
    }

    onFormDataChange(prev => ({
      ...prev,
      max_group_size: value
    }));

    // Validate immediately
    const errors = validateField('max_group_size', value);
    if (errors.max_group_size) {
      setValidationErrors(prev => ({
        ...prev,
        max_group_size: errors.max_group_size
      }));
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.max_group_size;
        return newErrors;
      });
    }
  };

  // Logo upload handler
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setLogoError('Please upload a valid image file (JPEG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setLogoError('File size should be less than 5MB');
      return;
    }

    setLogoError('');
    setLogoFile(file);

    // Create preview from file
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Store file info in form data
    const fileReader = new FileReader();
    fileReader.onloadend = () => {
      const base64String = fileReader.result;
      onFormDataChange(prev => ({
        ...prev,
        EveLogo_Base64: base64String,
        EveLogo_Name: file.name,
        EveLogo_Type: file.type,
        LogoPath_Url: '' // Clear existing URL when uploading new file
      }));
    };
    fileReader.readAsDataURL(file);

    // Clear logo error
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.logo;
      return newErrors;
    });
  };

  // Handle logo removal
  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setLogoError('');

    onFormDataChange(prev => ({
      ...prev,
      EveLogo_Base64: '',
      EveLogo_Name: '',
      EveLogo_Type: '',
      LogoPath_Url: '' // Clear logo URL
    }));

    // Set logo error if creating new event
    if (!editingEvent) {
      setValidationErrors(prev => ({
        ...prev,
        logo: 'Event logo is required'
      }));
    }
  };

  const handleSubmitWithValidation = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allFields = [
      'Event_Name',
      'Event_Sname',
      'Inst_Name',
      'Dept_id',
      'Organizer_id',
      'Event_type',
      'Event_mode',
      'Participant_type',
      'Start_datetime',
      'End_datetime',
      'PaymentStartDate',
      'PaymentEndDate',
      'Event_Place',
      'Event_Description',
      'max_group_size',
      'event_group_type',
      'sub_events'
    ];

    const touchedAllFields = {};
    allFields.forEach(field => {
      touchedAllFields[field] = true;
    });
    setTouchedFields(touchedAllFields);

    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const element = document.querySelector(`[data-field="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }

      Swal.fire({
        title: 'Validation Error!',
        html: `<div class="text-left mt-2">
          <p class="font-semibold text-red-600 mb-2">Please fix the following errors:</p>
          <ul class="space-y-1">
            ${Object.values(validationErrors).map(error => `
              <li class="text-red-600 flex items-start">
                <span class="inline-block w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                <span class="text-sm">${error}</span>
              </li>
            `).join('')}
          </ul>
        </div>`,
        icon: 'error',
        confirmButtonColor: '#EF4444',
        confirmButtonText: 'OK',
      });
      return;
    }

    // Prepare final form data
    const finalFormData = {
      ...formData,
      sub_events: showSubEvents ? selectedSubEvents : [],
      has_sub_events: showSubEvents ? 'yes' : 'no',
      sub_event_participants: showSubEvents ? subEventParticipantsConfig : {}
    };

    // Update form data
    onFormDataChange(finalFormData);

    // Wait a moment for state update, then submit
    setTimeout(() => {
      onSubmit(e);
    }, 100);
  };

  const selectedParticipantsLabel = useMemo(() => {
    // Don't show participant selection if sub-events are enabled
    if (showSubEvents) {
      return 'Sub-events selected';
    }

    const selected = Array.isArray(formData.Participant_type)
      ? formData.Participant_type
      : (formData.Participant_type ? [formData.Participant_type] : []);

    if (selected.length === 0 || (selected.length === 1 && selected[0] === '')) {
      return 'Select Participant Type';
    }

    const allParticipantIds = participantTypes.map(type => type.id.toString());
    const allSelected = allParticipantIds.every(id => selected.includes(id));

    if (allSelected) {
      return 'All Participant Types';
    }

    const names = selected.map(id => {
      const type = participantTypes.find(t => t.id.toString() === id.toString());
      return type ? type.name : '';
    }).filter(Boolean);

    return names.join(', ');
  }, [formData.Participant_type, participantTypes, showSubEvents]);

  const areAllParticipantsSelected = useMemo(() => {
    if (showSubEvents) return false;

    const selected = Array.isArray(formData.Participant_type)
      ? formData.Participant_type
      : (formData.Participant_type ? [formData.Participant_type] : []);

    const allParticipantIds = participantTypes.map(type => type.id.toString());
    return allParticipantIds.every(id => selected.includes(id));
  }, [formData.Participant_type, participantTypes, showSubEvents]);

  const getParticipantPricing = (participantId) => {
    if (!formData.participant_pricing || !Array.isArray(formData.participant_pricing)) {
      return { amount: '', max_participants: '', unlimited: false };
    }

    const participantIdStr = participantId.toString();
    const pricing = formData.participant_pricing.find(
      p => p.participant_id === participantIdStr || p.participant_id === participantId
    );

    return pricing || { amount: '', max_participants: '', unlimited: false };
  };

  const updateParticipantPricing = (participantId, field, value) => {
    let updatedPricing = [...(formData.participant_pricing || [])];
    const participantIdStr = participantId.toString();

    const index = updatedPricing.findIndex(
      p => p.participant_id === participantIdStr || p.participant_id === participantId
    );

    if (index >= 0) {
      updatedPricing[index] = {
        ...updatedPricing[index],
        [field]: value
      };
    } else {
      updatedPricing.push({
        participant_id: participantIdStr,
        amount: '',
        max_participants: '',
        unlimited: false,
        [field]: value
      });
    }

    onFormDataChange(prev => ({
      ...prev,
      participant_pricing: updatedPricing
    }));

    // Clear validation error if pricing is being set
    if (field === 'amount' || field === 'max_participants') {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.participant_pricing;
        return newErrors;
      });
    }
  };

  const handleAmountChange = (participantId, value) => {
    if (!/^\d*\.?\d*$/.test(value)) return;

    if (value === '') {
      updateParticipantPricing(participantId, 'amount', '');
      return;
    }

    if (value.length > 1 && value.startsWith('0') && !value.includes('.')) {
      value = value.replace(/^0+/, '');
    }

    updateParticipantPricing(participantId, 'amount', value);
  };

  const handleMaxParticipantsChange = (participantId, value) => {
    if (!/^\d*$/.test(value)) return;

    if (value === '') {
      updateParticipantPricing(participantId, 'max_participants', '');
      return;
    }

    if (value.length > 1 && value.startsWith('0')) {
      value = value.replace(/^0+/, '');
    }

    updateParticipantPricing(participantId, 'max_participants', value);
  };

  // Handle click outside for participant dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (participantDropdownRef.current && !participantDropdownRef.current.contains(event.target)) {
        setIsParticipantOpen(false);
      }
      if (subEventDropdownRef.current && !subEventDropdownRef.current.contains(event.target)) {
        setIsSubEventDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch event group types
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const response = await getData('/Dropdown/GetGroupDetails');

        if (!response?.isError && Array.isArray(response?.result)) {
          setEventGroupTypes(response.result);
        } else {
          setEventGroupTypes([]);
        }
      } catch (error) {
        console.error('Error fetching group details:', error);
        setEventGroupTypes([]);
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch department types
  useEffect(() => {
    const fetchDeptDropdownData = async () => {
      try {
        let category = selectedInstituteCategory;

        // Handle edit mode: derive category from Inst_Name if not already set
        if (!category && formData?.Inst_Name && institutes?.length) {
          const selectedInstitute = institutes.find(inst =>
            inst.id?.toString() === formData.Inst_Name?.toString() ||
            inst.Inst_Id?.toString() === formData.Inst_Name?.toString()
          );
          category = selectedInstitute?.Category || selectedInstitute?.category || '';

          if (category) {
            setSelectedInstituteCategory(category);
          }
        }

        if (!category) {
          setDepartmentTypes([]);
          return;
        }

        const response = await getData(`Dropdown/GetDepartment?category=${category}`);

        if (!response?.isError && Array.isArray(response?.result)) {
          setDepartmentTypes(response.result);

          // After departments are loaded, set the department value
          if (formData?.Dept_id && formData.Dept_id !== '') {
            // The department value will be automatically selected because we set it in formData
            // No need to do anything extra here
          }
        } else {
          setDepartmentTypes([]);
        }

      } catch (error) {
        console.error('Error fetching department details:', error);
        setDepartmentTypes([]);
      }
    };

    fetchDeptDropdownData();

  }, [selectedInstituteCategory, formData.Inst_Name, institutes, formData.Dept_id]); // Add formData.Dept_id as dependency

  const handleInputChange = (field, value) => {
    onFormDataChange(prev => ({
      ...prev,
      [field]: value
    }));

    // If institute is being changed, update the category state
    if (field === 'Inst_Name') {
      const selectedInstitute = institutes.find(inst =>
        inst.id.toString() === value.toString() ||
        inst.Inst_Id?.toString() === value.toString()
      );


      if (selectedInstitute) {
        setSelectedInstituteCategory(selectedInstitute.Category || selectedInstitute.category || '');
      } else {
        setSelectedInstituteCategory('');
      }
    }

    // Mark field as touched
    markFieldTouched(field);

    // Validate field
    const errors = validateField(field, value);
    if (errors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: errors[field]
      }));
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle sub-events toggle
  const handleSubEventsToggle = (value) => {
    const newShowSubEvents = value === 'yes';
    setShowSubEvents(newShowSubEvents);

    onFormDataChange(prev => ({
      ...prev,
      has_sub_events: value,
      Participant_type: newShowSubEvents ? [] : prev.Participant_type,
      participant_pricing: newShowSubEvents ? [] : prev.participant_pricing
    }));

    // Mark field as touched
    markFieldTouched('has_sub_events');

    // Clear validation errors
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      if (newShowSubEvents) {
        delete newErrors.Participant_type;
        delete newErrors.participant_pricing;
      } else {
        delete newErrors.sub_events;
        delete newErrors.sub_event_participants;
      }
      return newErrors;
    });
  };

  // Handle sub-event selection
  const handleSubEventToggle = (eventId) => {
    const eventIdStr = eventId.toString();
    const isSelected = selectedSubEvents.some(event =>
      event.id?.toString() === eventIdStr ||
      event.tempId?.toString() === eventIdStr ||
      event.Event_id?.toString() === eventIdStr
    );

    if (isSelected) {
      setSelectedSubEvents(prev => prev.filter(event =>
        event.id?.toString() !== eventIdStr &&
        event.tempId?.toString() !== eventIdStr &&
        event.Event_id?.toString() !== eventIdStr
      ));

      // Remove participant configuration for this sub-event
      setSubEventParticipantsConfig(prev => {
        const newConfig = { ...prev };
        // Try to delete by both id and tempId
        delete newConfig[eventIdStr];
        // Also try to find by original ID
        const keyToDelete = Object.keys(newConfig).find(key =>
          newConfig[key]?.originalSubEventId?.toString() === eventIdStr
        );
        if (keyToDelete) {
          delete newConfig[keyToDelete];
        }
        return newConfig;
      });

      // Update form data
      onFormDataChange(prev => ({
        ...prev,
        sub_events: selectedSubEvents.filter(event =>
          event.id?.toString() !== eventIdStr &&
          event.tempId?.toString() !== eventIdStr &&
          event.Event_id?.toString() !== eventIdStr
        )
      }));
    } else {
      const eventToAdd = filteredAvailableEvents.find(event =>
        event.id?.toString() === eventIdStr ||
        event.Event_id?.toString() === eventIdStr
      );
      if (eventToAdd) {
        const newSelectedEvents = [...selectedSubEvents, eventToAdd];
        setSelectedSubEvents(newSelectedEvents);

        // Update form data
        onFormDataChange(prev => ({
          ...prev,
          sub_events: newSelectedEvents
        }));
      }
    }

    // Clear sub-events validation error
    if (validationErrors.sub_events) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sub_events;
        return newErrors;
      });
    }
  };

  // Handle select all sub-events
  const handleSelectAllSubEvents = () => {
    if (selectedSubEvents.length === filteredAvailableEvents.length) {
      setSelectedSubEvents([]);
      setSubEventParticipantsConfig({});
      onFormDataChange(prev => ({
        ...prev,
        sub_events: [],
        sub_event_participants: {}
      }));
    } else {
      setSelectedSubEvents([...filteredAvailableEvents]);
      onFormDataChange(prev => ({
        ...prev,
        sub_events: [...filteredAvailableEvents]
      }));
    }

    // Clear sub-events validation error
    if (validationErrors.sub_events) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sub_events;
        return newErrors;
      });
    }
  };

  // Remove a specific sub-event
  const handleRemoveSubEvent = (eventId) => {
    const eventIdStr = eventId.toString();
    const newSelectedEvents = selectedSubEvents.filter(event =>
      event.id?.toString() !== eventIdStr &&
      event.tempId?.toString() !== eventIdStr &&
      event.Event_id?.toString() !== eventIdStr
    );

    setSelectedSubEvents(newSelectedEvents);

    // Remove participant configuration for this sub-event
    setSubEventParticipantsConfig(prev => {
      const newConfig = { ...prev };
      // Try to delete by both id and tempId
      delete newConfig[eventIdStr];
      // Also try to find by original ID
      const keyToDelete = Object.keys(newConfig).find(key =>
        newConfig[key]?.originalSubEventId?.toString() === eventIdStr
      );
      if (keyToDelete) {
        delete newConfig[keyToDelete];
      }
      return newConfig;
    });

    // Update form data
    onFormDataChange(prev => ({
      ...prev,
      sub_events: newSelectedEvents
    }));
  };

  // Open sub-event participant configuration popup
  const handleConfigureSubEventParticipants = (event) => {
    const eventId = event.id || event.tempId || event.Event_id;
    const eventIdStr = eventId.toString();


    // Initialize config for this sub-event if it doesn't exist
    if (!subEventParticipantsConfig[eventIdStr]) {
      const newConfig = {
        eventId: eventIdStr,
        originalSubEventId: event.originalSubEventId,
        eventName: event.Event_Name || event.name,
        selectedParticipants: [],
        participantPricing: {},
        index: event.index
      };
      setSubEventParticipantsConfig(prev => ({
        ...prev,
        [eventIdStr]: newConfig
      }));

      onFormDataChange(prev => ({
        ...prev,
        sub_event_participants: {
          ...prev.sub_event_participants,
          [eventIdStr]: newConfig
        }
      }));

      setCurrentSubEventConfig(newConfig);
    } else {
      setCurrentSubEventConfig(subEventParticipantsConfig[eventIdStr]);
    }

    setShowSubEventConfigPopup(true);
  };

  // Handle participant selection in sub-event popup
  const handleSubEventParticipantToggle = (participantId) => {
    if (!currentSubEventConfig) return;

    const participantIdStr = participantId.toString();
    const currentSelected = currentSubEventConfig.selectedParticipants || [];

    const updatedSelected = currentSelected.includes(participantIdStr)
      ? currentSelected.filter(id => id !== participantIdStr)
      : [...currentSelected, participantIdStr];

    // Initialize pricing for this participant if not exists
    const updatedPricing = { ...currentSubEventConfig.participantPricing };
    if (!currentSelected.includes(participantIdStr) && !updatedPricing[participantIdStr]) {
      updatedPricing[participantIdStr] = {
        amount: '',
        max_participants: '',
        unlimited: false
      };
    }

    const updatedConfig = {
      ...currentSubEventConfig,
      selectedParticipants: updatedSelected,
      participantPricing: updatedPricing
    };

    setCurrentSubEventConfig(updatedConfig);
  };

  // Handle select all participants in sub-event popup
  const handleSelectAllSubEventParticipants = () => {
    if (!currentSubEventConfig) return;

    const allParticipantIds = participantTypes.map(type => type.id.toString());
    const currentSelected = currentSubEventConfig.selectedParticipants || [];

    if (currentSelected.length === allParticipantIds.length) {
      // Deselect all
      setCurrentSubEventConfig({
        ...currentSubEventConfig,
        selectedParticipants: [],
        participantPricing: {}
      });
    } else {
      // Select all
      const newPricing = {};
      allParticipantIds.forEach(id => {
        newPricing[id] = currentSubEventConfig.participantPricing[id] || {
          amount: '',
          max_participants: '',
          unlimited: false
        };
      });

      setCurrentSubEventConfig({
        ...currentSubEventConfig,
        selectedParticipants: allParticipantIds,
        participantPricing: newPricing
      });
    }
  };

  // Handle pricing change in sub-event popup
  const handleSubEventPricingChange = (participantId, field, value) => {
    if (!currentSubEventConfig) return;

    const participantIdStr = participantId.toString();
    const updatedPricing = {
      ...currentSubEventConfig.participantPricing,
      [participantIdStr]: {
        ...currentSubEventConfig.participantPricing[participantIdStr],
        [field]: value
      }
    };

    setCurrentSubEventConfig({
      ...currentSubEventConfig,
      participantPricing: updatedPricing
    });
  };

  // Save sub-event participant configuration
  const handleSaveSubEventConfig = () => {
    if (!currentSubEventConfig) return;

    // Validate sub-event configuration
    const validationErrors = [];

    // Check if at least one participant is selected
    if (!currentSubEventConfig.selectedParticipants || currentSubEventConfig.selectedParticipants.length === 0) {
      validationErrors.push("Select at least one participant type");
    }

    // Check each selected participant for validation errors
    if (currentSubEventConfig.selectedParticipants && currentSubEventConfig.selectedParticipants.length > 0) {
      currentSubEventConfig.selectedParticipants.forEach(participantId => {
        const pricing = currentSubEventConfig.participantPricing?.[participantId] || {};

        // Check max participants (either unlimited OR valid number)
        if (!pricing.unlimited && (!pricing.max_participants || pricing.max_participants.trim() === '')) {
          const participantType = participantTypes.find(t => t.id.toString() === participantId);
          validationErrors.push(`Set max participants or check "Unlimited" for ${participantType?.name || 'selected participant'}`);
        } else if (!pricing.unlimited && !/^\d+$/.test(pricing.max_participants)) {
          const participantType = participantTypes.find(t => t.id.toString() === participantId);
          validationErrors.push(`Max participants must be a number for ${participantType?.name || 'selected participant'}`);
        } else if (!pricing.unlimited && parseInt(pricing.max_participants) < 1) {
          const participantType = participantTypes.find(t => t.id.toString() === participantId);
          validationErrors.push(`Max participants must be at least 1 for ${participantType?.name || 'selected participant'}`);
        }

        // Check amount
        if (!pricing.amount || pricing.amount.trim() === '') {
          const participantType = participantTypes.find(t => t.id.toString() === participantId);
          validationErrors.push(`Enter amount for ${participantType?.name || 'selected participant'}`);
        } else if (!/^\d+(\.\d{1,2})?$/.test(pricing.amount)) {
          const participantType = participantTypes.find(t => t.id.toString() === participantId);
          validationErrors.push(`Invalid amount format for ${participantType?.name || 'selected participant'}`);
        } else if (parseFloat(pricing.amount) < 0) {
          const participantType = participantTypes.find(t => t.id.toString() === participantId);
          validationErrors.push(`Amount cannot be negative for ${participantType?.name || 'selected participant'}`);
        }
      });
    }

    if (validationErrors.length > 0) {
      Swal.fire({
        title: 'Configuration Error!',
        html: `<div class="text-left mt-2">
          <p class="font-semibold text-red-600 mb-2">Please fix the following errors:</p>
          <ul class="space-y-1">
            ${validationErrors.map(error => `
              <li class="text-red-600 flex items-start">
                <span class="inline-block w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                <span class="text-sm">${error}</span>
              </li>
            `).join('')}
          </ul>
        </div>`,
        icon: 'error',
        confirmButtonColor: '#EF4444',
        confirmButtonText: 'OK',
      });
      return;
    }

    const updatedConfig = {
      ...subEventParticipantsConfig,
      [currentSubEventConfig.eventId]: currentSubEventConfig
    };

    setSubEventParticipantsConfig(updatedConfig);

    // Update form data
    onFormDataChange(prev => ({
      ...prev,
      sub_event_participants: updatedConfig
    }));

    setShowSubEventConfigPopup(false);
    setCurrentSubEventConfig(null);

    // Clear sub-event participants validation error
    if (validationErrors.sub_event_participants) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sub_event_participants;
        return newErrors;
      });
    }

    Swal.fire({
      title: 'Success!',
      text: 'Sub-event participants configured successfully',
      icon: 'success',
      confirmButtonColor: '#10B981',
      confirmButtonText: 'OK',
    });
  };

  // Check if all participants selected in sub-event popup
  const areAllSubEventParticipantsSelected = useMemo(() => {
    if (!currentSubEventConfig) return false;

    const allParticipantIds = participantTypes.map(type => type.id.toString());
    const currentSelected = currentSubEventConfig.selectedParticipants || [];

    return allParticipantIds.every(id => currentSelected.includes(id));
  }, [currentSubEventConfig, participantTypes]);

  // Check if a participant is selected in sub-event popup
  const isSubEventParticipantSelected = (participantId) => {
    if (!currentSubEventConfig) return false;

    const participantIdStr = participantId.toString();
    return (currentSubEventConfig.selectedParticipants || []).includes(participantIdStr);
  };

  const getSubEventParticipantConfig = (eventId) => {
    if (!eventId) {
      return {
        eventId: '',
        selectedParticipants: [],
        participantPricing: {}
      };
    }

    const eventIdStr = eventId.toString();
    const config = subEventParticipantsConfig[eventIdStr];

    if (!config) {
      return {
        eventId: eventIdStr,
        selectedParticipants: [],
        participantPricing: {}
      };
    }

    return config;
  };

  // Get selected participant names for a sub-event
  const getSubEventSelectedParticipantNames = (eventId) => {
    const config = getSubEventParticipantConfig(eventId);

    if (!config.selectedParticipants || config.selectedParticipants.length === 0) {
      return 'No participants selected';
    }

    const allParticipantIds = participantTypes.map(type => type.id.toString());
    if (config.selectedParticipants.length === allParticipantIds.length) {
      return 'All participant types';
    }

    return config.selectedParticipants.map(id => {
      const type = participantTypes.find(t => t.id?.toString() === id);
      return type ? type.name : '';
    }).filter(Boolean).join(', ');
  };


  const handleSelectAllToggle = () => {
    const allParticipantIds = participantTypes.map(type => type.id.toString());

    if (areAllParticipantsSelected) {
      handleInputChange('Participant_type', []);
    } else {
      handleInputChange('Participant_type', allParticipantIds);
    }

    // Clear validation error
    if (validationErrors.Participant_type) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.Participant_type;
        return newErrors;
      });
    }
  };

  const handleParticipantTypeToggle = (typeId) => {
    const currentTypes = Array.isArray(formData.Participant_type)
      ? formData.Participant_type
      : (formData.Participant_type ? [formData.Participant_type] : []);

    const filteredTypes = currentTypes.filter(type => type !== '');
    const typeIdStr = typeId.toString();

    const updatedTypes = filteredTypes.includes(typeIdStr)
      ? filteredTypes.filter(id => id.toString() !== typeIdStr)
      : [...filteredTypes, typeIdStr];

    handleInputChange('Participant_type', updatedTypes);

    // Clear validation error
    if (validationErrors.Participant_type) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.Participant_type;
        return newErrors;
      });
    }
  };

  const isParticipantSelected = (participantId) => {
    const selected = Array.isArray(formData.Participant_type)
      ? formData.Participant_type
      : (formData.Participant_type ? [formData.Participant_type] : []);

    const participantIdStr = participantId.toString();
    return selected.includes(participantIdStr);
  };

  const getParticipantColor = (participantId) => {
    const colors = {
      1: { bg: 'bg-blue-100', text: 'text-blue-800', icon: 'text-blue-600', border: 'border-blue-200' },
      2: { bg: 'bg-purple-100', text: 'text-purple-800', icon: 'text-purple-600', border: 'border-purple-200' },
      3: { bg: 'bg-green-100', text: 'text-green-800', icon: 'text-green-600', border: 'border-green-200' },
      4: { bg: 'bg-amber-100', text: 'text-amber-800', icon: 'text-amber-600', border: 'border-amber-200' }
    };
    return colors[participantId] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: 'text-gray-600', border: 'border-gray-200' };
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    if (dateString.includes("0001-01-01")) {
      return "";
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date)) return "";
      return date.toISOString().split("T")[0];
    } catch (error) {
      return "";
    }
  };

  const parseDateFromInput = (dateString, time = '00:00') => {
    if (!dateString) return '';
    return `${dateString}T${time}:00`;
  };

  const calculateTotalParticipants = () => {
    if (!formData.participant_pricing || !Array.isArray(formData.participant_pricing)) {
      return 0;
    }

    return formData.participant_pricing.reduce((total, pricing) => {
      return total + (parseInt(pricing.max_participants) || 0);
    }, 0);
  };

  const handleUnlimitedChange = (participantId, checked) => {
    let updatedPricing = [...(formData.participant_pricing || [])];
    const participantIdStr = participantId.toString();

    const index = updatedPricing.findIndex(
      p => p.participant_id === participantIdStr || p.participant_id === participantId
    );

    if (index >= 0) {
      updatedPricing[index] = {
        ...updatedPricing[index],
        unlimited: checked,
        max_participants: checked ? null : (updatedPricing[index].max_participants || '')
      };
    } else {
      updatedPricing.push({
        participant_id: participantIdStr,
        amount: '',
        max_participants: null,
        unlimited: checked
      });
    }

    onFormDataChange(prev => ({
      ...prev,
      participant_pricing: updatedPricing
    }));
  };

  const areEventDatesValid = () => {
    return formData.Start_datetime && formData.End_datetime;
  };

  // Handle Event Date Changes with validation
  const handleEventStartDateChange = (dateValue) => {
    const newStartDate = parseDateFromInput(dateValue, '09:00');

    onFormDataChange(prev => {
      const updatedData = {
        ...prev,
        Start_datetime: dateValue ? newStartDate : ''
      };

      // If clearing start date, also clear end date and payment dates
      if (!dateValue) {
        updatedData.End_datetime = '';
        updatedData.PaymentStartDate = '';
        updatedData.PaymentEndDate = '';
      }
      // If new start date is after current end date, reset end date and payment dates
      else if (eventEndDate && dateValue > eventEndDate) {
        updatedData.End_datetime = '';
        updatedData.PaymentStartDate = '';
        updatedData.PaymentEndDate = '';
      }

      return updatedData;
    });

    // Validate field
    const errors = validateField('Start_datetime', dateValue ? newStartDate : '');
    if (errors.Start_datetime) {
      setValidationErrors(prev => ({
        ...prev,
        Start_datetime: errors.Start_datetime
      }));
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.Start_datetime;
        return newErrors;
      });
    }
  };

  // Handle Event End Date Changes with validation
  const handleEventEndDateChange = (dateValue) => {
    const newEndDate = parseDateFromInput(dateValue, '18:00');

    onFormDataChange(prev => {
      const updatedData = {
        ...prev,
        End_datetime: dateValue ? newEndDate : ''
      };

      // If clearing end date, also clear payment dates
      if (!dateValue) {
        updatedData.PaymentStartDate = '';
        updatedData.PaymentEndDate = '';
      }

      return updatedData;
    });

    // Validate field
    const errors = validateField('End_datetime', dateValue ? newEndDate : '');
    if (errors.End_datetime) {
      setValidationErrors(prev => ({
        ...prev,
        End_datetime: errors.End_datetime
      }));
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.End_datetime;
        return newErrors;
      });
    }
  };

  // Handle Payment Start Date Changes with validation
  const handlePaymentStartDateChange = (dateValue) => {
    // First check if event dates are set
    if (!areEventDatesValid()) {
      Swal.fire({
        title: 'Event Dates Required',
        text: 'Please set event start and end dates first',
        icon: 'warning',
        confirmButtonColor: '#F59E0B',
        confirmButtonText: 'OK',
      });
      return;
    }

    const newPaymentStartDate = parseDateFromInput(dateValue, '18:00');
    onFormDataChange(prev => ({
      ...prev,
      PaymentStartDate: newPaymentStartDate
    }));

    // Validate field
    const errors = validateField('PaymentStartDate', newPaymentStartDate);
    if (errors.PaymentStartDate) {
      setValidationErrors(prev => ({
        ...prev,
        PaymentStartDate: errors.PaymentStartDate
      }));
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.PaymentStartDate;
        return newErrors;
      });
    }
  };

  // Handle Payment End Date Changes with validation
  const handlePaymentEndDateChange = (dateValue) => {
    if (!areEventDatesValid()) {
      Swal.fire({
        title: 'Event Dates Required',
        text: 'Please set event start and end dates first',
        icon: 'warning',
        confirmButtonColor: '#F59E0B',
        confirmButtonText: 'OK',
      });
      return;
    }

    const newPaymentEndDate = parseDateFromInput(dateValue, '23:59');

    if (!paymentStartDate || dateValue >= paymentStartDate) {
      onFormDataChange(prev => ({
        ...prev,
        PaymentEndDate: newPaymentEndDate
      }));

      // Validate field
      const errors = validateField('PaymentEndDate', newPaymentEndDate);
      if (errors.PaymentEndDate) {
        setValidationErrors(prev => ({
          ...prev,
          PaymentEndDate: errors.PaymentEndDate
        }));
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.PaymentEndDate;
          return newErrors;
        });
      }
    }
  };

  // Check if field should show error
  const shouldShowError = (field) => {
    return touchedFields[field] && validationErrors[field];
  };

  const minPaymentDate = formData.PaymentStartDate
    ? formatDateForInput(formData.PaymentStartDate)
    : getTodayDate();

  const maxPaymentDate = formData.EventStartDate
    ? new Date(new Date(formData.EventStartDate).getTime() - 86400000)
      .toISOString()
      .split('T')[0]
    : '';

  // Move the conditional return to the end, after all hooks
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 backdrop-blur-sm animate-fadeIn">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto animate-slideUp">
          <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-purple-50 rounded-t-2xl border-b border-gray-200 p-4 z-10">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-blue-500 rounded-lg">
                  <Sparkles size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {editingEvent ? 'Edit Event' : 'Create New Event'}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {editingEvent ? 'Update your event details' : 'Fill in all the event information below'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white rounded-lg transition-all duration-200"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmitWithValidation} className="p-4">
            <div className="space-y-6">
              {/* Event Logo Upload Section */}
              <div className="bg-gradient-to-br from-indigo-50 to-violet-50 p-6 rounded-xl border border-indigo-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-lg flex items-center justify-center mr-3">
                      <ImageIcon size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Event Logo</h3>
                      <p className="text-sm text-gray-600">Upload a logo that represents your event</p>
                    </div>
                  </div>
                  {(logoPreview || logoFile) && (
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="flex items-center text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      <Trash2 size={16} className="mr-1" />
                      Remove Logo
                    </button>
                  )}
                </div>

                <div className="relative">
                  <label htmlFor="event-logo" className="block cursor-pointer group">
                    <div className="bg-white w-full border-2 border-dashed border-gray-300 hover:border-indigo-300 rounded-xl p-3 transition-all duration-200 hover:shadow-md">
                      <div className="flex flex-col lg:flex-row items-center gap-8">
                        <div className="w-full">
                          <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200 group-hover:border-indigo-200 transition-colors">
                            {logoPreview ? (
                              <div className="relative">
                                <img
                                  src={logoPreview}
                                  alt="Event logo preview"
                                  className="w-full h-48 object-contain"
                                />
                                <div className="absolute inset-0 bg-indigo-600 bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-200 rounded-lg"></div>
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg">
                                    Change Logo
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center h-48">
                                <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-violet-100 rounded-full flex items-center justify-center mb-4">
                                  <ImageIcon size={32} className="text-indigo-500" />
                                </div>
                                <p className="text-sm text-gray-600">No logo uploaded</p>
                                <p className="text-xs text-gray-500 mt-1">Preview will appear here</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <input
                      id="event-logo"
                      type="file"
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleLogoUpload}
                      onClick={(e) => markFieldTouched('logo')}
                    />
                  </label>
                </div>

                {validationErrors.logo && (
                  <div className="mt-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-red-100 text-red-600 rounded-lg flex items-center justify-center text-sm font-medium mr-3 flex-shrink-0">
                        !
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-red-800">Logo Required</p>
                        <p className="text-sm text-red-700 mt-1">{validationErrors.logo}</p>
                      </div>
                    </div>
                  </div>
                )}

                {logoError && (
                  <div className="mt-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-red-100 text-red-600 rounded-lg flex items-center justify-center text-sm font-medium mr-3 flex-shrink-0">
                        !
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-red-800">Upload Error</p>
                        <p className="text-sm text-red-700 mt-1">{logoError}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Event Information Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <FileText size={16} className="mr-2 text-blue-600" />
                  Event Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2" data-field="Event_Name">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      <span>Event Name *</span>
                      {shouldShowError('Event_Name') && (
                        <span className="text-xs font-normal text-red-500">Required</span>
                      )}
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.Event_Name || ''}
                      maxLength={120}
                      onChange={(e) => handleInputChange('Event_Name', e.target.value)}
                      onBlur={() => markFieldTouched('Event_Name')}
                      className={`w-full px-3 py-2 text-sm outline-none border rounded-lg focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 ${shouldShowError('Event_Name') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Enter full event name (min. 3 characters)"
                    />
                    {shouldShowError('Event_Name') && (
                      <p className="text-xs text-red-500">{validationErrors.Event_Name}</p>
                    )}
                  </div>

                  <div className="space-y-2" data-field="Event_Sname">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      <span>Short Name *</span>
                      {shouldShowError('Event_Sname') && (
                        <span className="text-xs font-normal text-red-500">Required</span>
                      )}
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.Event_Sname || ''}
                      onChange={(e) => handleInputChange('Event_Sname', e.target.value)}
                      onBlur={() => markFieldTouched('Event_Sname')}
                      className={`w-full px-3 py-2 text-sm outline-none border rounded-lg focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 ${shouldShowError('Event_Sname') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Enter short event name (min. 2 characters)"
                    />
                    {shouldShowError('Event_Sname') && (
                      <p className="text-xs text-red-500">{validationErrors.Event_Sname}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2" data-field="Inst_Name">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      <span>Institute Name *</span>
                      {shouldShowError('Inst_Name') && (
                        <span className="text-xs font-normal text-red-500">Required</span>
                      )}
                    </label>
                    <select
                      required
                      value={formData.Inst_Name || ''}
                      onChange={(e) => handleInputChange('Inst_Name', e.target.value)}
                      onBlur={() => markFieldTouched('Inst_Name')}
                      className={`w-full px-3 py-2 text-sm outline-none border rounded-lg focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 ${shouldShowError('Inst_Name') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                        }`}
                    >
                      <option value="">Select Institute</option>
                      {institutes && institutes.map(institute => (
                        <option key={institute.id} value={institute.id}>
                          {institute.name}
                        </option>
                      ))}
                    </select>
                    {shouldShowError('Inst_Name') && (
                      <p className="text-xs text-red-500">{validationErrors.Inst_Name}</p>
                    )}
                  </div>

                  {/* Nitheesh */}
                  <div className="space-y-2" data-field="Dept_id">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      <span>Department Name *</span>
                      {shouldShowError('Dept_id') && (
                        <span className="text-xs font-normal text-red-500">Required</span>
                      )}
                    </label>
                    <select
                      required
                      value={formData.Dept_id || ''}
                      onChange={(e) => handleInputChange('Dept_id', e.target.value)}
                      onBlur={() => markFieldTouched('Dept_id')}
                      className={`w-full px-3 py-2 text-sm outline-none border rounded-lg focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 ${shouldShowError('Dept_id') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                        }`}
                    >
                      <option value="">Select Department</option>
                      {departmentTypes && departmentTypes.map(dept => (
                        <option key={dept.Dept_ID} value={dept.Dept_ID}>
                          {dept.Dept_Name}
                        </option>
                      ))}
                    </select>
                    {shouldShowError('Dept_id') && (
                      <p className="text-xs text-red-500">{validationErrors.Dept_id}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Organizer & Group Event Section */}
              <div className="bg-gradient-to-br from-cyan-50 to-sky-50 p-4 rounded-xl border border-cyan-100">
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <UserCircle size={16} className="mr-2 text-cyan-600" />
                  Organizer Details
                </h3>

                {/* Group Event Radio Options */}
                <div className="mb-4 p-3 bg-white rounded-lg border border-cyan-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <GroupIcon size={14} className="mr-2 text-cyan-600" />
                      <label className="text-sm font-medium text-gray-900">
                        Is this a Group Event? *
                      </label>
                    </div>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="groupEvent"
                          value="no"
                          checked={formData.is_group_event === 'no'}
                          onChange={(e) => handleGroupEventChange(e.target.value)}
                          className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300"
                        />
                        <span className="text-sm text-gray-700">No</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="groupEvent"
                          value="yes"
                          checked={formData.is_group_event === 'yes'}
                          onChange={(e) => handleGroupEventChange(e.target.value)}
                          className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300"
                        />
                        <span className="text-sm text-gray-700">Yes</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2" data-field="Organizer_id">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      <span>Event Organizer *</span>
                      {shouldShowError('Organizer_id') && (
                        <span className="text-xs font-normal text-red-500">Required</span>
                      )}
                    </label>
                    <div className="relative">
                      <UserCircle size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-cyan-500" />
                      <select
                        required
                        value={formData.Organizer_id || ''}
                        onChange={(e) => handleInputChange('Organizer_id', e.target.value)}
                        onBlur={() => markFieldTouched('Organizer_id')}
                        className={`w-full pl-8 pr-3 py-2 text-sm outline-none border rounded-lg focus:ring-1 focus:ring-cyan-500 bg-white transition-all duration-200 ${shouldShowError('Organizer_id') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                      >
                        <option value="">Select Event Organizer</option>
                        {organizers && organizers.map(organizer => (
                          <option key={organizer.id} value={organizer.id}>
                            {organizer.id}-{organizer.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {shouldShowError('Organizer_id') && (
                      <p className="text-xs text-red-500">{validationErrors.Organizer_id}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Sub-events Section */}
              {formData.is_group_event === 'no' && <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-4 rounded-xl border border-teal-100" data-field="sub_events">
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <Plus size={16} className="mr-2 text-teal-600" />
                  Sub-events Configuration
                </h3>

                <div className="mb-4 p-3 bg-white rounded-lg border border-teal-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <Sparkles size={14} className="mr-2 text-teal-600" />
                      <label className="text-sm font-medium text-gray-900">
                        Do you want to add sub-events?
                      </label>
                    </div>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="hasSubEvents"
                          value="no"
                          checked={!showSubEvents}
                          onChange={(e) => handleSubEventsToggle(e.target.value)}
                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
                        />
                        <span className="text-sm text-gray-700">No</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="hasSubEvents"
                          value="yes"
                          checked={showSubEvents}
                          onChange={(e) => handleSubEventsToggle(e.target.value)}
                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
                        />
                        <span className="text-sm text-gray-700">Yes</span>
                      </label>
                    </div>
                  </div>
                </div>

                {showSubEvents && (
                  <div className="mt-4" ref={subEventDropdownRef}>
                    <div className="space-y-2 mb-3">
                      <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                        <span>Select Sub-events *</span>
                        {shouldShowError('sub_events') && (
                          <span className="text-xs font-normal text-red-500">Required</span>
                        )}
                        {!shouldShowError('sub_events') && (
                          <span className="text-xs font-normal text-gray-500">
                            {selectedSubEvents.length} event(s) selected
                          </span>
                        )}
                      </label>

                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsSubEventDropdownOpen(!isSubEventDropdownOpen)}
                          onBlur={() => markFieldTouched('sub_events')}
                          className={`w-full text-left bg-white border rounded-lg p-2 min-h-10 flex items-center justify-between focus:outline-none focus:ring-1 focus:ring-teal-500 transition-colors hover:border-teal-400 ${shouldShowError('sub_events') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                            }`}
                        >
                          <span className="text-xs truncate flex items-center gap-1 w-full">
                            <span className="truncate flex-1 text-gray-700">
                              {selectedSubEvents.length === 0
                                ? 'Select sub-events'
                                : `${selectedSubEvents.length} event(s) selected`}
                            </span>
                            {selectedSubEvents.length > 0 && (
                              <span className="flex-shrink-0 px-1.5 py-0.5 bg-teal-100 text-teal-700 text-xs font-semibold rounded">
                                {selectedSubEvents.length} selected
                              </span>
                            )}
                          </span>
                          <svg
                            className={`h-3 w-3 text-gray-500 transition-transform duration-200 ${isSubEventDropdownOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {shouldShowError('sub_events') && (
                          <p className="text-xs text-red-500 mt-1">{validationErrors.sub_events}</p>
                        )}

                        {isSubEventDropdownOpen && (
                          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {isLoadingSubEvents ? (
                              <div className="p-4 text-center">
                                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600 mr-2"></div>
                                <span className="text-xs text-gray-600">Loading sub-events...</span>
                              </div>
                            ) : filteredAvailableEvents.length > 0 ? (
                              <>
                                <label
                                  htmlFor="subevent-select-all"
                                  className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-teal-50 transition border-b border-gray-200 ${selectedSubEvents.length === filteredAvailableEvents.length ? 'bg-teal-100' : ''}`}
                                >
                                  <div className="flex items-center">
                                    <input
                                      id="subevent-select-all"
                                      type="checkbox"
                                      checked={selectedSubEvents.length === filteredAvailableEvents.length}
                                      onChange={handleSelectAllSubEvents}
                                      className="peer hidden"
                                    />
                                    <div
                                      className={`w-4 h-4 mr-2 rounded border flex items-center justify-center transition ${selectedSubEvents.length === filteredAvailableEvents.length ? 'bg-teal-500 border-teal-500' : 'border-gray-300'} peer-checked:border-teal-600 peer-checked:bg-teal-500`}
                                    >
                                      {selectedSubEvents.length === filteredAvailableEvents.length && (
                                        <svg
                                          className="w-2.5 h-2.5 text-white"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                          strokeWidth="3"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </div>
                                    <span className="block text-xs font-semibold text-gray-700">Select All</span>
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {filteredAvailableEvents.length} events available
                                  </span>
                                </label>
                                {filteredAvailableEvents.map(event => {
                                  const eventId = event.id || event.Event_id;
                                  const eventName = event.Event_Name || event.name;
                                  const eventShortName = event.Event_Sname || event.short_name;

                                  const isSelected = selectedSubEvents.some(selected => {
                                    const selectedId = selected.id || selected.Event_id;
                                    return selectedId?.toString() === eventId?.toString();
                                  });

                                  return (
                                    <label
                                      key={eventId}
                                      htmlFor={`subevent-${eventId}`}
                                      className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-teal-50 transition ${isSelected ? 'bg-teal-100' : ''}`}
                                    >
                                      <div className="flex items-center">
                                        <input
                                          id={`subevent-${eventId}`}
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => handleSubEventToggle(eventId)}
                                          className="peer hidden"
                                        />
                                        <div
                                          className={`w-4 h-4 mr-2 rounded border flex items-center justify-center transition ${isSelected ? 'bg-teal-500 border-teal-500' : 'border-gray-300'} peer-checked:border-teal-600 peer-checked:bg-teal-500`}
                                        >
                                          {isSelected && (
                                            <svg
                                              className="w-2.5 h-2.5 text-white"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                              strokeWidth="3"
                                            >
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                          )}
                                        </div>
                                        <div className="flex flex-col">
                                          <span className="block text-xs font-medium text-gray-700">
                                            {eventName}
                                          </span>
                                          {eventShortName && (
                                            <span className="block text-xs text-gray-500">
                                              {eventShortName}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      {isSelected && (
                                        <div className="flex-shrink-0">
                                          <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                                        </div>
                                      )}
                                    </label>
                                  );
                                })}
                              </>
                            ) : (
                              <div className="p-4 text-center">
                                <p className="text-xs text-gray-600">No sub-events available</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {filteredAvailableEvents.length === 0 && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-xs text-gray-600 text-center">
                            No events available to add as sub-events
                          </p>
                        </div>
                      )}
                    </div>

                    {selectedSubEvents.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-xs font-semibold text-gray-900 mb-2">Selected Sub-events:</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto p-2">
                          {selectedSubEvents.map(event => {
                            const eventId = event.id || event.Event_id;
                            const eventName = event.Event_Name || event.name;
                            const eventShortName = event.Event_Sname || event.short_name;
                            const participantConfig = getSubEventParticipantConfig(eventId);

                            return (
                              <div
                                key={eventId}
                                className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-lg hover:bg-teal-50 transition-colors"
                              >
                                <div className="flex items-center">
                                  <div className="w-6 h-6 bg-teal-100 text-teal-600 rounded flex items-center justify-center mr-2">
                                    <Sparkles size={12} />
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-gray-800">
                                      {eventName}
                                    </p>
                                    {eventShortName && (
                                      <p className="text-xs text-gray-500">
                                        {eventShortName}
                                      </p>
                                    )}
                                    <div className="mt-1">
                                      <span className="text-xs text-gray-600">
                                        Participants: {getSubEventSelectedParticipantNames(eventId)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => handleConfigureSubEventParticipants(event)}
                                    className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                    title="Configure participants"
                                  >
                                    <Users size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveSubEvent(eventId)}
                                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                    title="Remove sub-event"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {validationErrors.sub_event_participants && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-600">{validationErrors.sub_event_participants}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>}

              {/* Event Details Section */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100">
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <Sparkles size={16} className="mr-2 text-green-600" />
                  Event Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2" data-field="Event_type">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      <span>Event Type *</span>
                      {shouldShowError('Event_type') && (
                        <span className="text-xs font-normal text-red-500">Required</span>
                      )}
                    </label>
                    <select
                      required
                      value={formData.Event_type || ''}
                      onChange={(e) => handleInputChange('Event_type', e.target.value)}
                      onBlur={() => markFieldTouched('Event_type')}
                      className={`w-full px-3 py-2 text-sm outline-none border rounded-lg focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 ${shouldShowError('Event_type') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                        }`}
                    >
                      <option value="">Select Event Type</option>
                      {filteredEventTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                    {shouldShowError('Event_type') && (
                      <p className="text-xs text-red-500">{validationErrors.Event_type}</p>
                    )}
                    {filteredEventTypes.length === 0 && (
                      <p className="text-xs text-red-500 mt-1">
                        No event types available for this selection
                      </p>
                    )}
                  </div>

                  <div className="space-y-2" data-field="Event_mode">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      <span>Event Mode *</span>
                      {shouldShowError('Event_mode') && (
                        <span className="text-xs font-normal text-red-500">Required</span>
                      )}
                    </label>
                    <select
                      required
                      value={formData.Event_mode || ''}
                      onChange={(e) => handleInputChange('Event_mode', e.target.value)}
                      onBlur={() => markFieldTouched('Event_mode')}
                      className={`w-full px-3 py-2 text-sm outline-none border rounded-lg focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 ${shouldShowError('Event_mode') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                        }`}
                    >
                      <option value="">Select Event Mode</option>
                      {eventModes.map(mode => (
                        <option key={mode.id} value={mode.id}>{mode.name}</option>
                      ))}
                    </select>
                    {shouldShowError('Event_mode') && (
                      <p className="text-xs text-red-500">{validationErrors.Event_mode}</p>
                    )}
                  </div>

                  {/* Participant Type - Hide when sub-events are enabled */}
                  {!showSubEvents && (
                    <div className="space-y-2" ref={participantDropdownRef} data-field="Participant_type">
                      <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                        <span>Participant Type *</span>
                        {shouldShowError('Participant_type') && (
                          <span className="text-xs font-normal text-red-500">Required</span>
                        )}
                      </label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsParticipantOpen(prev => !prev)}
                          onBlur={() => markFieldTouched('Participant_type')}
                          className={`w-full text-left bg-white border rounded-lg p-2 min-h-10 flex items-center justify-between focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${shouldShowError('Participant_type') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                            }`}
                        >
                          <span className="text-xs truncate flex items-center gap-1 w-full">
                            <span className="truncate flex-1 text-gray-700">
                              {selectedParticipantsLabel}
                            </span>
                            {formData.Participant_type && formData.Participant_type.length > 0 && formData.Participant_type[0] !== '' && (
                              <span className="flex-shrink-0 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                {areAllParticipantsSelected ? 'All' : formData.Participant_type.length} selected
                              </span>
                            )}
                          </span>
                          <svg
                            className={`h-3 w-3 text-gray-500 transition-transform duration-200 ${isParticipantOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {shouldShowError('Participant_type') && (
                          <p className="text-xs text-red-500 mt-1">{validationErrors.Participant_type}</p>
                        )}

                        {isParticipantOpen && (
                          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            <label
                              htmlFor="participant-select-all"
                              className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-blue-50 transition border-b border-gray-200 ${areAllParticipantsSelected ? 'bg-blue-100' : ''}`}
                            >
                              <div className="flex items-center">
                                <input
                                  id="participant-select-all"
                                  type="checkbox"
                                  checked={areAllParticipantsSelected}
                                  onChange={handleSelectAllToggle}
                                  className="peer hidden"
                                />
                                <div
                                  className={`w-4 h-4 mr-2 rounded border flex items-center justify-center transition ${areAllParticipantsSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'} peer-checked:border-blue-600 peer-checked:bg-blue-500`}
                                >
                                  {areAllParticipantsSelected && (
                                    <svg
                                      className="w-2.5 h-2.5 text-white"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth="3"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                                <span className="block text-xs font-semibold text-gray-700">Select All</span>
                              </div>
                            </label>

                            {participantTypes.map(type => {
                              const isSelected = isParticipantSelected(type.id);
                              return (
                                <label
                                  key={type.id}
                                  htmlFor={`participant-${type.id}`}
                                  className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-blue-50 transition ${isSelected ? 'bg-blue-100' : ''}`}
                                >
                                  <div className="flex items-center">
                                    <input
                                      id={`participant-${type.id}`}
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleParticipantTypeToggle(type.id)}
                                      className="peer hidden"
                                    />
                                    <div
                                      className={`w-4 h-4 mr-2 rounded border flex items-center justify-center transition ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'} peer-checked:border-blue-600 peer-checked:bg-blue-500`}
                                    >
                                      {isSelected && (
                                        <svg
                                          className="w-2.5 h-2.5 text-white"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                          strokeWidth="3"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </div>
                                    <span className="block text-xs text-gray-700">{type.name}</span>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Show message when sub-events are enabled */}
                  {showSubEvents && (
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-900">
                        Participant Type
                      </label>
                      <div className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="text-xs text-gray-600 text-center">
                          Configure participant types individually for each sub-event
                        </p>
                        <p className="text-xs text-green-600 text-center mt-1">
                          {selectedSubEvents.length} sub-event(s) selected
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Group Event Type & Maximum Group Size Section */}
                {formData.is_group_event === 'yes' && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-green-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2" data-field="event_group_type">
                        <label className="text-sm font-semibold text-gray-900 flex items-center justify-between">
                          <div className="flex items-center">
                            <GroupIcon size={14} className="mr-2 text-green-600" />
                            Group Event Type *
                          </div>
                          {shouldShowError('event_group_type') && (
                            <span className="text-xs font-normal text-red-500">Required</span>
                          )}
                        </label>
                        <select
                          required
                          value={formData.event_group_type || ''}
                          onChange={(e) => handleInputChange('event_group_type', e.target.value)}
                          onBlur={() => markFieldTouched('event_group_type')}
                          className={`w-full px-3 py-2 text-sm outline-none border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white transition-all duration-200 ${shouldShowError('event_group_type') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                            }`}
                        >
                          <option value="">Select Group Type</option>
                          {eventGroupTypes.map(type => (
                            <option key={type.Group_ID} value={type.Group_ID}>
                              {type.Group_Name}
                            </option>
                          ))}
                        </select>
                        {shouldShowError('event_group_type') && (
                          <p className="text-xs text-red-500">{validationErrors.event_group_type}</p>
                        )}
                      </div>

                      <div className="space-y-2" data-field="max_group_size">
                        <label className="text-sm font-semibold text-gray-900 flex items-center justify-between">
                          <div className="flex items-center">
                            <Users size={14} className="mr-2 text-blue-600" />
                            Maximum Group Size *
                          </div>
                          {shouldShowError('max_group_size') && (
                            <span className="text-xs font-normal text-red-500">Required</span>
                          )}
                        </label>
                        <div className="relative">
                          <Users size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500" />
                          <input
                            type="text"
                            required
                            value={formData.max_group_size || ''}
                            onChange={(e) => handleMaxGroupSizeChange(e.target.value)}
                            onBlur={() => markFieldTouched('max_group_size')}
                            className={`w-full pl-10 pr-3 py-2 text-sm outline-none border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all duration-200 ${shouldShowError('max_group_size') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                              }`}
                            placeholder="Enter maximum group size (2-100)"
                          />
                        </div>
                        {shouldShowError('max_group_size') && (
                          <p className="text-xs text-red-500">{validationErrors.max_group_size}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Event Dates Section */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-xl border border-orange-100">
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <Calendar size={16} className="mr-2 text-orange-600" />
                  Event Dates
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2" data-field="Start_datetime">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      <span>Start Date *</span>
                      {shouldShowError('Start_datetime') && (
                        <span className="text-xs font-normal text-red-500">Required</span>
                      )}
                    </label>
                    <div className="relative">
                      <Calendar size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-orange-500" />
                      <input
                        type="date"
                        required
                        value={formatDateForInput(formData.Start_datetime)}
                        onChange={(e) => handleEventStartDateChange(e.target.value)}
                        onBlur={() => markFieldTouched('Start_datetime')}
                        min={getTodayDate()}
                        className={`w-full pl-8 pr-3 py-2 text-sm outline-none border rounded-lg focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 ${shouldShowError('Start_datetime') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                      />
                    </div>
                    {shouldShowError('Start_datetime') && (
                      <p className="text-xs text-red-500">{validationErrors.Start_datetime}</p>
                    )}
                  </div>

                  <div className="space-y-2" data-field="End_datetime">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      <span>End Date *</span>
                      {shouldShowError('End_datetime') && (
                        <span className="text-xs font-normal text-red-500">Required</span>
                      )}
                    </label>
                    <div className="relative">
                      <Calendar size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-red-500" />
                      <input
                        type="date"
                        required
                        value={formatDateForInput(formData.End_datetime)}
                        onChange={(e) => handleEventEndDateChange(e.target.value)}
                        onBlur={() => markFieldTouched('End_datetime')}
                        min={eventStartDate || getTodayDate()}
                        className={`w-full pl-8 pr-3 py-2 text-sm outline-none border rounded-lg focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 ${shouldShowError('End_datetime') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                      />
                    </div>
                    {shouldShowError('End_datetime') && (
                      <p className="text-xs text-red-500">{validationErrors.End_datetime}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Dates Section */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 rounded-xl border border-purple-100">
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <Calendar size={16} className="mr-2 text-purple-600" />
                  Payment Dates
                </h3>
                {!areEventDatesValid() && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center">
                      <div className="bg-yellow-100 p-1.5 rounded mr-2">
                        <Calendar size={14} className="text-yellow-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-800">
                          Set Event Dates First
                        </p>
                        <p className="text-xs text-yellow-700">
                          Please set event start and end dates to enable payment date selection
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2" data-field="PaymentStartDate">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      <span>Payment Start Date *</span>
                      {shouldShowError('PaymentStartDate') && (
                        <span className="text-xs font-normal text-red-500">Required</span>
                      )}
                    </label>
                    <div className="relative">
                      <Calendar size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-purple-500" />
                      <input
                        type="date"
                        required
                        value={formatDateForInput(formData.PaymentStartDate)}
                        onChange={(e) => handlePaymentStartDateChange(e.target.value)}
                        onBlur={() => markFieldTouched('PaymentStartDate')}
                        min={minPaymentDate}
                        max={maxPaymentDate}
                        className={`w-full pl-8 pr-3 py-2 text-sm outline-none border rounded-lg focus:ring-1 focus:ring-blue-500 transition-all duration-200
                          ${!areEventDatesValid() ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} ${shouldShowError('PaymentStartDate') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                      />
                    </div>
                    {shouldShowError('PaymentStartDate') && (
                      <p className="text-xs text-red-500">{validationErrors.PaymentStartDate}</p>
                    )}
                  </div>

                  <div className="space-y-2" data-field="PaymentEndDate">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      <span>Payment End Date *</span>
                      {shouldShowError('PaymentEndDate') && (
                        <span className="text-xs font-normal text-red-500">Required</span>
                      )}
                    </label>
                    <div className="relative">
                      <Calendar size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-indigo-500" />
                      <input
                        type="date"
                        required
                        value={formatDateForInput(formData.PaymentEndDate)}
                        onChange={(e) => handlePaymentEndDateChange(e.target.value)}
                        onBlur={() => markFieldTouched('PaymentEndDate')}
                        min={paymentStartDate || getTodayDate()}
                        max={eventEndDate || ''}
                        disabled={!areEventDatesValid() || !formData.PaymentStartDate}
                        className={`w-full pl-8 pr-3 py-2 text-sm outline-none border rounded-lg focus:ring-1 focus:ring-blue-500 transition-all duration-200
                          ${!areEventDatesValid() || !formData.PaymentStartDate ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} ${shouldShowError('PaymentEndDate') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                      />
                    </div>
                    {shouldShowError('PaymentEndDate') && (
                      <p className="text-xs text-red-500">{validationErrors.PaymentEndDate}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Location Details Section */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-100" data-field="Event_Place">
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <MapPin size={16} className="mr-2 text-purple-600" />
                  Location Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      <span>Event Place *</span>
                      {shouldShowError('Event_Place') && (
                        <span className="text-xs font-normal text-red-500">Required</span>
                      )}
                    </label>
                    <div className="relative">
                      <MapPin size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-purple-500" />
                      <input
                        type="text"
                        required
                        value={formData.Event_Place || ''}
                        onChange={(e) => handleInputChange('Event_Place', e.target.value)}
                        onBlur={() => markFieldTouched('Event_Place')}
                        className={`w-full pl-8 pr-3 py-2 text-sm outline-none border rounded-lg focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 ${shouldShowError('Event_Place') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter event venue (min. 3 characters)"
                      />
                    </div>
                    {shouldShowError('Event_Place') && (
                      <p className="text-xs text-red-500">{validationErrors.Event_Place}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-900 flex items-center justify-between">
                      GPS Coordinates
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.GPS_coordinates || ''}
                        onChange={(e) => handleInputChange('GPS_coordinates', e.target.value)}
                        className="w-full px-3 py-2 text-sm border outline-none border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200"
                        placeholder="lat,long"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Participants & Pricing Section - Only show when NOT using sub-events */}
              {!showSubEvents && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-200" data-field="participant_pricing">
                  <div className="flex items-center mb-4">
                    <div className="bg-amber-100 p-1.5 rounded mr-2">
                      <Users size={14} className="text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Participants & Pricing</h3>
                      <p className="text-xs text-gray-600">Configure participant limits and pricing</p>
                    </div>
                  </div>

                  {(!formData.Participant_type ||
                    (Array.isArray(formData.Participant_type) && formData.Participant_type.length === 0)) && (
                      <div className="text-center py-4 px-3 bg-white rounded-lg border border-amber-200">
                        <Users size={20} className="mx-auto text-amber-400 mb-2" />
                        <h4 className="text-sm font-medium text-gray-700 mb-1">No Participant Type Selected</h4>
                        <p className="text-xs text-gray-500">
                          Please select at least one participant type to configure pricing and limits.
                        </p>
                      </div>
                    )}

                  {participantTypes
                    .filter(type => isParticipantSelected(type.id))
                    .map(type => {
                      const color = getParticipantColor(type.id);
                      const pricing = getParticipantPricing(type.id);

                      return (
                        <div key={type.id} className="mb-4 p-3 bg-white rounded-lg border" style={{ borderColor: color.border.replace('border-', '').replace('-200', '') }}>
                          <div className="flex items-center mb-3">
                            <div className={`${color.bg} p-1 rounded mr-2`}>
                              <Users size={12} className={color.icon} />
                            </div>
                            <h4 className={`text-sm font-semibold ${color.text}`}>
                              {type.name}
                            </h4>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <label className="text-xs font-medium text-gray-700">
                                Max Participants *
                              </label>

                              <div className="flex items-center space-x-2">
                                <div className="relative flex-1">
                                  <Users
                                    size={12}
                                    className={`absolute left-2 top-1/2 transform -translate-y-1/2 ${pricing.unlimited ? "text-gray-400" : color.icon
                                      }`}
                                  />

                                  <input
                                    type="text"
                                    value={pricing.unlimited ? "Unlimited" : pricing.max_participants || ""}
                                    disabled={pricing.unlimited}
                                    className={`w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded
                    focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 outline-none
                    ${pricing.unlimited ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""}
                  `}
                                    onChange={(e) => handleMaxParticipantsChange(type.id, e.target.value)}
                                    placeholder={pricing.unlimited ? "Unlimited" : "Enter max participants"}
                                  />
                                </div>

                                <div className="flex items-center space-x-1">
                                  <input
                                    type="checkbox"
                                    id={`unlimited-${type.id}`}
                                    checked={pricing.unlimited || false}
                                    onChange={(e) => handleUnlimitedChange(type.id, e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <label
                                    htmlFor={`unlimited-${type.id}`}
                                    className="text-xs text-gray-700 select-none"
                                  >
                                    Unlimited
                                  </label>
                                </div>
                              </div>
                            </div>

                            <div className="space-y-1">
                              <label className="text-xs font-medium text-gray-700"> Event Amount (₹): ₹{((Number(pricing.amount) * 100) / 118).toFixed(2)} , GST (18%): ₹{(Number(pricing.amount) - (Number(pricing.amount) * 100) / 118).toFixed(2)} </label>

                              <div className="relative">
                                <IndianRupee size={12} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-green-500" />
                                <input
                                  type="text"
                                  value={pricing.amount || ''}
                                  onChange={(e) => handleAmountChange(type.id, e.target.value)}
                                  className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 outline-none"
                                  placeholder="Enter amount"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                  {validationErrors.participant_pricing && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs text-red-600">{validationErrors.participant_pricing}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Show message when sub-events are enabled instead of Participants & Pricing */}
              {showSubEvents && (
                <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-4 rounded-xl border border-teal-200">
                  <div className="flex items-center mb-4">
                    <div className="bg-teal-100 p-1.5 rounded mr-2">
                      <Users size={14} className="text-teal-600" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Participants & Pricing</h3>
                      <p className="text-xs text-gray-600">Configured individually for each sub-event</p>
                    </div>
                  </div>

                  <div className="p-4 bg-white rounded-lg border border-teal-200">
                    <div className="">
                      <div className="bg-teal-50 p-2 rounded mr-3">
                        <Sparkles size={16} className="text-teal-500" />
                      </div>
                      <div >
                        <h4 className="text-sm font-medium text-gray-800 mb-1">
                          Sub-event Participant Configuration
                        </h4>
                        <p className="text-xs text-gray-600 mb-2">
                          Each sub-event can have its own participant types, pricing, and limits.
                          Click the <Users size={12} className="inline text-blue-500" /> icon next to each sub-event to configure.
                        </p>

                        {selectedSubEvents.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-700 mb-2">
                              Configured Sub-events ({selectedSubEvents.length}):
                            </p>
                            <div className="space-y-2 max-h-32 overflow-y-auto px-6">
                              {selectedSubEvents.map(event => {
                                const eventId = event.id || event.Event_id;
                                const config = getSubEventParticipantConfig(eventId);

                                return (
                                  <div
                                    key={eventId}
                                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                                  >
                                    <div>
                                      <p className="text-xs font-medium text-gray-800">
                                        {event.Event_Name || event.name}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {config.selectedParticipants.length} participant type(s) configured
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleConfigureSubEventParticipants(event)}
                                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                      {config.selectedParticipants.length > 0 ? 'Edit' : 'Configure'}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Event Management Section */}
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-4 rounded-xl border border-gray-200">
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <Link size={16} className="mr-2 text-blue-600" />
                  Event Management
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-900">
                      Event Status *
                    </label>
                    <select
                      value={formData.Event_status || '1'}
                      onChange={(e) => handleInputChange('Event_status', e.target.value)}
                      className="w-full px-3 py-2 text-sm outline-none border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200"
                    >
                      {eventStatuses?.map((status) => (
                        <option key={status.Status_ID} value={status.Status_ID}>
                          {status.Status_Detail}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Description Section */}
              <div className="space-y-4" data-field="Event_Description">
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-xl border border-indigo-100">
                  <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText size={16} className="mr-2 text-indigo-600" />
                      Event Description *
                    </div>
                    {shouldShowError('Event_Description') && (
                      <span className="text-xs font-normal text-red-500">Required</span>
                    )}
                  </h3>
                  <textarea
                    required
                    value={formData.Event_Description || ''}
                    onChange={(e) => handleInputChange('Event_Description', e.target.value)}
                    onBlur={() => markFieldTouched('Event_Description')}
                    rows="3"
                    className={`w-full px-3 py-2 outline-none text-sm border rounded-lg focus:ring-1 focus:ring-blue-500 bg-white resize-none transition-all duration-200 ${shouldShowError('Event_Description') ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Describe your event in detail (min. 10 characters)"
                  />
                  {shouldShowError('Event_Description') && (
                    <p className="text-xs text-red-500 mt-1">{validationErrors.Event_Description}</p>
                  )}
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.Event_Description?.length || 0}/2000 characters
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-sm text-gray-700 hover:text-gray-900 font-semibold bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-semibold rounded-lg transition-all duration-200 shadow transform hover:scale-105"
              >
                <div className="flex items-center space-x-1">
                  <Sparkles size={14} />
                  <span>{editingEvent ? 'Update Event' : 'Create Event'}</span>
                </div>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Sub-event Participant Configuration Popup */}
      {showSubEventConfigPopup && currentSubEventConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-[60] backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto animate-slideUp">
            <div className="sticky top-0 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-t-2xl border-b border-gray-200 p-4 z-10">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-teal-500 rounded-lg">
                    <Users size={18} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Configure Participants for: {currentSubEventConfig.eventName}
                    </h2>
                    <p className="text-sm text-gray-600">
                      Select participant types and configure pricing for this sub-event
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowSubEventConfigPopup(false);
                    setCurrentSubEventConfig(null);
                  }}
                  className="p-2 hover:bg-white rounded-lg transition-all duration-200"
                >
                  <X size={20} className="text-gray-600" />
                </button>
              </div>
            </div>

            <div className="p-4">
              {/* Participant Type Selection */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Select Participant Types *
                  </h3>
                  <button
                    type="button"
                    onClick={handleSelectAllSubEventParticipants}
                    className="text-xs text-teal-600 hover:text-teal-800 font-medium"
                  >
                    {areAllSubEventParticipantsSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {participantTypes.map(type => {
                    const isSelected = isSubEventParticipantSelected(type.id);
                    const color = getParticipantColor(type.id);
                    const pricing = currentSubEventConfig.participantPricing?.[type.id.toString()] || {};
                    const hasMaxParticipantsError = isSelected && !pricing.unlimited && (!pricing.max_participants || pricing.max_participants.trim() === '');
                    const hasAmountError = isSelected && (!pricing.amount || pricing.amount.trim() === '');

                    return (
                      <label
                        key={type.id}
                        htmlFor={`subevent-participant-${type.id}`}
                        className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all duration-200 ${isSelected ?
                          (hasMaxParticipantsError || hasAmountError ? 'bg-red-50 border-red-300' : color.bg + ' ' + color.border) :
                          'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                      >
                        <input
                          id={`subevent-participant-${type.id}`}
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSubEventParticipantToggle(type.id)}
                          className="peer hidden"
                        />
                        <div
                          className={`w-5 h-5 mr-3 rounded border flex items-center justify-center transition ${isSelected ?
                            (hasMaxParticipantsError || hasAmountError ? 'bg-red-500 border-red-500' : 'bg-teal-500 border-teal-500') :
                            'border-gray-300'
                            } peer-checked:border-teal-600 peer-checked:bg-teal-500`}
                        >
                          {isSelected && (
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth="3"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <span className={`text-sm font-medium ${isSelected ?
                            (hasMaxParticipantsError || hasAmountError ? 'text-red-700' : color.text) :
                            'text-gray-700'
                            }`}>
                            {type.name}
                          </span>
                          {isSelected && (hasMaxParticipantsError || hasAmountError) && (
                            <div className="mt-1">
                              {hasMaxParticipantsError && (
                                <span className="text-xs text-red-600 block">• Set max participants or check unlimited</span>
                              )}
                              {hasAmountError && (
                                <span className="text-xs text-red-600 block">• Amount is required</span>
                              )}
                            </div>
                          )}
                        </div>
                        {isSelected && !hasMaxParticipantsError && !hasAmountError && (
                          <div className={`w-2 h-2 rounded-full ${color.bg.replace('bg-', '').replace('-100', '')}`}></div>
                        )}
                      </label>
                    );
                  })}
                </div>

                {currentSubEventConfig.selectedParticipants?.length === 0 && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700 font-medium">
                      <span className="inline-flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        Please select at least one participant type
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Pricing Configuration for Selected Participants */}
              {currentSubEventConfig.selectedParticipants && currentSubEventConfig.selectedParticipants.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">
                    Configure Pricing & Limits *
                  </h3>

                  <div className="space-y-4">
                    {participantTypes
                      .filter(type => currentSubEventConfig.selectedParticipants.includes(type.id.toString()))
                      .map(type => {
                        const pricing = currentSubEventConfig.participantPricing[type.id.toString()] || {
                          amount: '',
                          max_participants: '',
                          unlimited: false
                        };
                        const color = getParticipantColor(type.id);
                        const hasMaxParticipantsError = !pricing.unlimited && (!pricing.max_participants || pricing.max_participants.trim() === '');
                        const hasAmountError = !pricing.amount || pricing.amount.trim() === '';

                        return (
                          <div
                            key={type.id}
                            className={`p-3 rounded-lg border ${hasMaxParticipantsError || hasAmountError ? 'bg-red-50 border-red-300' : 'bg-white'
                              }`}
                            style={{
                              borderColor: hasMaxParticipantsError || hasAmountError ?
                                '#fca5a5' :
                                color.border.replace('border-', '').replace('-200', '')
                            }}
                          >
                            <div className="flex items-center mb-3">
                              <div className={`${color.bg} p-1 rounded mr-2`}>
                                <Users size={12} className={color.icon} />
                              </div>
                              <h4 className={`text-sm font-semibold ${hasMaxParticipantsError || hasAmountError ? 'text-red-700' : color.text
                                }`}>
                                {type.name}
                              </h4>
                              {(hasMaxParticipantsError || hasAmountError) && (
                                <div className="ml-2 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                                  Required
                                </div>
                              )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">
                                  Max Participants *
                                </label>

                                <div className="flex items-center space-x-2">
                                  <div className="relative flex-1">
                                    <Users
                                      size={12}
                                      className={`absolute left-2 top-1/2 transform -translate-y-1/2 ${pricing.unlimited ? "text-gray-400" :
                                        hasMaxParticipantsError ? "text-red-500" : color.icon
                                        }`}
                                    />

                                    <input
                                      type="text"
                                      value={pricing.unlimited ? "Unlimited" : pricing.max_participants || ""}
                                      disabled={pricing.unlimited}
                                      onChange={(e) => {
                                        if (!/^\d*$/.test(e.target.value)) return;
                                        let value = e.target.value;
                                        if (value.length > 1 && value.startsWith('0')) {
                                          value = value.replace(/^0+/, '');
                                        }
                                        handleSubEventPricingChange(type.id, 'max_participants', value);
                                      }}
                                      className={`w-full pl-7 pr-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 outline-none ${pricing.unlimited ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""
                                        } ${hasMaxParticipantsError ? "border-red-500 focus:ring-red-500" : "border-gray-300"
                                        }`}
                                      placeholder={pricing.unlimited ? "Unlimited" : "Enter max participants"}
                                    />
                                  </div>

                                  <div className="flex items-center space-x-1">
                                    <input
                                      type="checkbox"
                                      id={`subevent-unlimited-${type.id}`}
                                      checked={pricing.unlimited || false}
                                      onChange={(e) => {
                                        handleSubEventPricingChange(type.id, 'unlimited', e.target.checked);
                                      }}
                                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label
                                      htmlFor={`subevent-unlimited-${type.id}`}
                                      className="text-xs text-gray-700 select-none"
                                    >
                                      Unlimited
                                    </label>
                                  </div>
                                </div>

                                {hasMaxParticipantsError && !pricing.unlimited && (
                                  <p className="text-xs text-red-500 mt-1">
                                    Please enter max participants or check "Unlimited"
                                  </p>
                                )}
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">
                                  Amount (₹) *
                                </label>

                                <div className="relative">
                                  <IndianRupee
                                    size={12}
                                    className={`absolute left-2 top-1/2 transform -translate-y-1/2 ${hasAmountError ? "text-red-500" : "text-green-500"
                                      }`}
                                  />
                                  <input
                                    type="text"
                                    value={pricing.amount || ''}
                                    onChange={(e) => {
                                      if (!/^\d*\.?\d*$/.test(e.target.value)) return;
                                      let value = e.target.value;
                                      if (value.length > 1 && value.startsWith('0') && !value.includes('.')) {
                                        value = value.replace(/^0+/, '');
                                      }
                                      handleSubEventPricingChange(type.id, 'amount', value);
                                    }}
                                    className={`w-full pl-7 pr-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-blue-500 bg-white transition-all duration-200 outline-none ${hasAmountError ? "border-red-500 focus:ring-red-500" : "border-gray-300"
                                      }`}
                                    placeholder="Enter amount"
                                  />
                                </div>

                                {hasAmountError && (
                                  <p className="text-xs text-red-500 mt-1">
                                    Please enter the amount
                                  </p>
                                )}

                                {pricing.amount && !hasAmountError && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Total: ₹{pricing.amount} (₹{((Number(pricing.amount) * 100) / 118).toFixed(2)} + ₹{(Number(pricing.amount) - (Number(pricing.amount) * 100) / 118).toFixed(2)} GST)
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Validation Summary */}
              {(() => {
                const validationErrors = [];

                // Check if at least one participant is selected
                if (!currentSubEventConfig.selectedParticipants || currentSubEventConfig.selectedParticipants.length === 0) {
                  validationErrors.push("Select at least one participant type");
                }

                // Check each selected participant for validation errors
                if (currentSubEventConfig.selectedParticipants && currentSubEventConfig.selectedParticipants.length > 0) {
                  currentSubEventConfig.selectedParticipants.forEach(participantId => {
                    const pricing = currentSubEventConfig.participantPricing?.[participantId] || {};

                    // Check max participants (either unlimited OR valid number)
                    if (!pricing.unlimited && (!pricing.max_participants || pricing.max_participants.trim() === '')) {
                      const participantType = participantTypes.find(t => t.id.toString() === participantId);
                      if (participantType) {
                        validationErrors.push(`Set max participants or check "Unlimited" for ${participantType.name}`);
                      }
                    } else if (!pricing.unlimited && !/^\d+$/.test(pricing.max_participants)) {
                      const participantType = participantTypes.find(t => t.id.toString() === participantId);
                      if (participantType) {
                        validationErrors.push(`Max participants must be a number for ${participantType.name}`);
                      }
                    } else if (!pricing.unlimited && parseInt(pricing.max_participants) < 1) {
                      const participantType = participantTypes.find(t => t.id.toString() === participantId);
                      if (participantType) {
                        validationErrors.push(`Max participants must be at least 1 for ${participantType.name}`);
                      }
                    }

                    // Check amount
                    if (!pricing.amount || pricing.amount.trim() === '') {
                      const participantType = participantTypes.find(t => t.id.toString() === participantId);
                      if (participantType) {
                        validationErrors.push(`Enter amount for ${participantType.name}`);
                      }
                    } else if (!/^\d+(\.\d{1,2})?$/.test(pricing.amount)) {
                      const participantType = participantTypes.find(t => t.id.toString() === participantId);
                      if (participantType) {
                        validationErrors.push(`Invalid amount format for ${participantType.name}`);
                      }
                    } else if (parseFloat(pricing.amount) < 0) {
                      const participantType = participantTypes.find(t => t.id.toString() === participantId);
                      if (participantType) {
                        validationErrors.push(`Amount cannot be negative for ${participantType.name}`);
                      }
                    }
                  });
                }

                return validationErrors.length > 0 ? (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center mb-2">
                      <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <h4 className="text-sm font-semibold text-red-800">Configuration Required</h4>
                    </div>
                    <ul className="text-sm text-red-700 space-y-1">
                      {validationErrors.map((error, index) => (
                        <li key={index} className="flex items-start">
                          <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                          <span>{error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null;
              })()}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowSubEventConfigPopup(false);
                    setCurrentSubEventConfig(null);
                  }}
                  className="px-6 py-2 text-sm text-gray-700 hover:text-gray-900 font-semibold bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveSubEventConfig}
                  className="px-6 py-2 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white text-sm font-semibold rounded-lg transition-all duration-200 shadow transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  disabled={(() => {
                    // Disable save button if there are validation errors
                    if (!currentSubEventConfig.selectedParticipants || currentSubEventConfig.selectedParticipants.length === 0) {
                      return true;
                    }

                    // Check each selected participant
                    return currentSubEventConfig.selectedParticipants.some(participantId => {
                      const pricing = currentSubEventConfig.participantPricing?.[participantId] || {};

                      // Check max participants (either unlimited OR valid number)
                      const hasMaxParticipantsError = !pricing.unlimited && (!pricing.max_participants || pricing.max_participants.trim() === '');

                      // Check amount
                      const hasAmountError = !pricing.amount || pricing.amount.trim() === '';

                      return hasMaxParticipantsError || hasAmountError;
                    });
                  })()}
                >
                  <div className="flex items-center space-x-1">
                    <Check size={14} />
                    <span>Save Configuration</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventFormModal;